import styled, { css } from "styled-components";

import Heading from "core/Heading";

import breakpoints from "core/breakpoints";
import colors from "core/colors";
import fonts from "core/fonts";
import { regular as regularFontWeight } from "core/fontStyles";
import { spacingWithBreakpoints } from "core/spacing";
import { P } from "core/Typography";
import { ButtonDefaults } from "core/Button/styles";

import { convertPxToRem } from "frontend/components/helpers";

const browserIsIE = window.document.documentMode;

/* general card components */
export const CardIconContainer = styled.div`
    display: inline-block;
`;

export const CardTitle = styled(Heading)``;

export const CardDescription = styled.p`
    ${regularFontWeight}

    /* Tablet: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        font-size: 1rem;
        line-height: 1.5rem;
    }

    /* Desktop: */
    @media (min-width: ${breakpoints.desktop.rem}) {
        font-size: 1.125rem;
        line-height: 1.8125rem;
    }
`;

export const CardHighlightsContainer = styled.ul`
    text-align: left;

    /* Mobile and tablet: */
    @media (max-width: ${breakpoints.beforeDesktop.rem}) {
        display: none;
    }
`;

export const CardHighlight = styled.li`
    font-size: 0.9rem; /* 14.4px custom */
    color: ${colors.fossil};

    &:not(:last-of-type) {
        padding-bottom: 0.375rem;
        border-bottom: 1px solid ${colors.concrete};
    }

    &:not(:first-of-type) {
        padding-top: 0.625rem;
    }
`;

export const CardCta = styled.a.attrs({
    size: "lg",
    justifyContent: "center"
})`
    && {
        @media (min-width: ${breakpoints.tablet.rem}) {
            ${ButtonDefaults}
            /* force the 48px height - issues with link being squished due to container padding: */
            height: auto;
            padding: ${convertPxToRem({ px: 16 })};
            padding-top: ${convertPxToRem({ px: 17 })};
        }
    }
`;

export const CardNonMobileContentContainer = styled.div`
    ${Card} & {
        & > *:not(:last-child) {
            ${spacingWithBreakpoints.LGBottom};
        }

        @media (max-width: ${breakpoints.beforeTablet.rem}) {
            display: none;
        }
    }
`;

export const CardMobileContentContainer = styled.div`
    ${Card} & {
        display: flex;
        align-items: center;
        text-align: left;
        line-height: 1.3rem;

        @media (min-width: ${breakpoints.tablet.rem}) {
            display: none;
        }

        & > *:not(:last-child) {
            margin-right: 1rem;
        }

        ${CardIconContainer} {
            width: 2.8125rem;
            height: 2.8125rem;
        }

        /* creates a link-style link instead of button-style: */
        ${CardCta} {
            border: none;
            font-size: 0.875rem;
            text-decoration: underline;
            cursor: pointer;
            padding: 0;
            color: ${colors.fossil};
        }
    }
`;

const bulletListCardStyles = css`
    padding: 1.5rem;
    text-align: left;

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        padding-right: 0;
    }
`;

/* card container component */
export const Card = styled.div`
    border: 1px solid
        ${({ darkMode }) => {
            return darkMode ? colors.cardDarkModeBorder : colors.concrete;
        }};
    text-align: center;
    color: ${colors.midnight};
    background-color: ${({ darkMode, transparent }) => {
        if (transparent) return "transparent";
        return darkMode ? colors.midnight : colors.snow;
    }};

    padding: 1rem;

    /* Tablet: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        padding: 2.5rem 0.875rem;
    }

    /* Desktop: */
    @media (min-width: ${breakpoints.desktop.rem}) {
        padding: 3rem 1.875rem;
    }

    /* applies padding for bullet list style
    cards on all screen sizes */
    @media (min-width: ${breakpoints.zero.rem}) {
        ${({ bulletList }) => bulletList && bulletListCardStyles}
    }
`;

/* bullet list card components */
export const CardBulletListTitle = styled(P).attrs({ bold: true })`
    font-size: 0.9rem; /* 14.4px custom */
`;

export const CardBulletListTitleIcon = styled.div`
    display: flex;
    align-items: center;
    width: 2rem;

    svg {
        width: 100%;
        height: auto;

        ${browserIsIE && "height: 28px;"}
    }
`;

export const CardBulletListTitleContainer = styled.div`
    display: flex;
    align-items: center;
    ${spacingWithBreakpoints.LGTopAndBottom}

    ${CardBulletListTitleIcon} {
        margin-right: 1rem;
    }
`;

export const CardBulletList = styled.ul`
    list-style-type: disc;
    ${fonts.typeScale[12]};
    margin-left: 1.75rem;

    li {
        padding-left: 1.25rem;

        &:not(:last-of-type) {
            padding-bottom: 0.875rem; /* 14px custom */
        }
    }
`;
